import { useEffect, useState, useMemo } from 'react';
import api from '../services/api';

const LEVEL_COLOR = {
  debug: ['#f1f5f9', '#64748b'],
  info:  ['#dbeafe', '#1e40af'],
  warn:  ['#fef9c3', '#854d0e'],
  error: ['#fee2e2', '#991b1b'],
};

const LEVEL_LABEL = {
  debug: 'Debug', info: 'Info', warn: 'Alerte', error: 'Erreur',
};

// Sources → noms français
const SOURCE_LABEL = {
  auth:          'Authentification',
  reviews:       'Avis',
  payments:      'Paiement',
  negotiations:  'Négociation',
  services:      'Service',
  users:         'Utilisateur',
  providers:     'Prestataire',
  clients:       'Client',
  notifications: 'Notification',
  system:        'Système',
  api:           'API',
  upload:        'Téléversement',
  email:         'Email',
  sms:           'SMS',
  admin:         'Admin',
  cron:          'Tâche planifiée',
};

// Combinaisons (source, message) → libellé clair en français
const MESSAGE_LABEL = {
  'auth.login_ok':       'Connexion réussie',
  'auth.login_fail':     'Échec de connexion',
  'auth.logout':         'Déconnexion',
  'auth.register':       'Nouvelle inscription',
  'auth.password_reset': 'Réinitialisation du mot de passe',

  'reviews.created':     'Avis publié',
  'reviews.updated':     'Avis modifié',
  'reviews.deleted':     'Avis supprimé',

  'payments.paid':       'Paiement effectué',
  'payments.refunded':   'Remboursement',
  'payments.failed':     'Paiement échoué',
  'payments.pending':    'Paiement en attente',

  'negotiations.created':  'Négociation ouverte',
  'negotiations.accepted': 'Négociation acceptée',
  'negotiations.refused':  'Négociation refusée',
  'negotiations.closed':   'Négociation clôturée',

  'services.created':    'Service créé',
  'services.updated':    'Service modifié',
  'services.published':  'Service publié',
  'services.archived':   'Service archivé',

  'users.created':       'Compte créé',
  'users.updated':       'Profil modifié',
  'users.deleted':       'Compte supprimé',

  'notifications.sent':  'Notification envoyée',
  'email.sent':          'Email envoyé',
  'email.failed':        'Email en échec',
  'sms.sent':            'SMS envoyé',
};

// Clés de contexte → libellés
const KEY_LABEL = {
  phone:          'Téléphone',
  email:          'Email',
  role:           'Rôle',
  user_id:        'Utilisateur',
  client_id:      'Client',
  provider_id:    'Prestataire',
  negotiation_id: 'Négociation',
  service_id:     'Service',
  review_id:      'Avis',
  reviewer:       'Évaluateur',
  rating:         'Note',
  amount:         'Montant',
  currency:       'Devise',
  method:         'Méthode',
  status:         'Statut',
  error:          'Erreur',
  ip:             'IP',
  device:         'Appareil',
  city:           'Ville',
  country:        'Pays',
  duration_ms:    'Durée',
  count:          'Nombre',
  reason:         'Raison',
  message:        'Message',
  category:       'Catégorie',
  type:           'Type',
  source:         'Source',
  target:         'Cible',
  created_by:     'Créé par',
  updated_by:     'Modifié par',
};

// Valeurs énumérées → traduction
const VALUE_LABEL = {
  role: { provider: 'Prestataire', client: 'Client', admin: 'Administrateur', super_admin: 'Super Admin' },
  status: { pending: 'En attente', paid: 'Payé', failed: 'Échoué', refunded: 'Remboursé', success: 'Réussi', cancelled: 'Annulé' },
  method: { mobile_money: 'Mobile Money', card: 'Carte bancaire', cash: 'Espèces', bank_transfer: 'Virement' },
};

function humanLabel(source, message) {
  const key = `${source}.${message}`;
  if (MESSAGE_LABEL[key]) return MESSAGE_LABEL[key];
  // fallback : message brut un peu nettoyé
  const cleaned = (message || '').replace(/[_-]/g, ' ');
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : '—';
}

function humanSource(source) {
  return SOURCE_LABEL[source] || (source ? source.charAt(0).toUpperCase() + source.slice(1) : '');
}

function formatValue(key, value) {
  if (value == null) return '—';
  // Valeurs énumérées
  if (VALUE_LABEL[key] && VALUE_LABEL[key][value] != null) {
    return VALUE_LABEL[key][value];
  }
  // Montants
  if (key === 'amount' || key === 'price' || key === 'total') {
    const n = Number(value);
    if (!Number.isNaN(n)) return n.toLocaleString('fr-FR') + ' FCFA';
  }
  // Note (1..5)
  if (key === 'rating') {
    const n = Number(value);
    if (!Number.isNaN(n) && n >= 0 && n <= 5) {
      return '★'.repeat(Math.round(n)) + '☆'.repeat(5 - Math.round(n)) + ` (${n}/5)`;
    }
  }
  // Durées en ms
  if (key === 'duration_ms') {
    const n = Number(value);
    if (!Number.isNaN(n)) return n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${n} ms`;
  }
  // IDs purs : préfixer #
  if (/_id$|^id$|^reviewer$|^created_by$|^updated_by$/.test(key) && (typeof value === 'number' || /^\d+$/.test(value))) {
    return `#${value}`;
  }
  // Objet / tableau imbriqué : sérialiser proprement
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

function humanKey(key) {
  return KEY_LABEL[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function parseContext(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
}

function ContextChips({ raw }) {
  const ctx = parseContext(raw);
  if (!ctx || (typeof ctx === 'object' && Object.keys(ctx).length === 0)) {
    return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>;
  }
  if (typeof ctx !== 'object' || Array.isArray(ctx)) {
    return <span>{String(ctx)}</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {Object.entries(ctx).map(([k, v]) => (
        <span key={k} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: '#f1f5f9', borderRadius: 6, padding: '2px 8px',
          fontSize: 11, fontFamily: 'system-ui, sans-serif',
        }}>
          <strong style={{ color: '#475569', fontWeight: 600 }}>{humanKey(k)} :</strong>
          <span style={{ color: '#0f172a' }}>{formatValue(k, v)}</span>
        </span>
      ))}
    </div>
  );
}

const fmtDate = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState('');
  const [q, setQ] = useState('');

  const load = () => {
    setLoading(true);
    const params = {};
    if (level) params.level = level;
    api.get('/admin/logs', { params })
      .then(({ data }) => setLogs(Array.isArray(data) ? data : []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [level]);

  const filtered = useMemo(() => {
    if (!q.trim()) return logs;
    const s = q.toLowerCase();
    return logs.filter(l =>
      (l.message || '').toLowerCase().includes(s) ||
      (l.source || '').toLowerCase().includes(s) ||
      humanLabel(l.source, l.message).toLowerCase().includes(s) ||
      humanSource(l.source).toLowerCase().includes(s)
    );
  }, [logs, q]);

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Logs serveur</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>200 derniers événements applicatifs · auto-refresh manuel</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={level} onChange={e => setLevel(e.target.value)} style={selectStyle}>
            <option value="">Tous niveaux</option>
            <option value="debug">Debug</option>
            <option value="info">Info</option>
            <option value="warn">Alerte</option>
            <option value="error">Erreur</option>
          </select>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher..." style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', minWidth: 240, fontSize: 13 }} />
          <button onClick={load} style={{ background: 'var(--primary)', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>↻ Rafraîchir</button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}><div className="spinner" /></div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              {filtered.length} log(s)
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                Aucun log {level ? `de niveau « ${LEVEL_LABEL[level]} »` : ''} pour le moment.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                    <th style={th}>Date</th>
                    <th style={th}>Niveau</th>
                    <th style={th}>Catégorie</th>
                    <th style={th}>Événement</th>
                    <th style={th}>Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(l => {
                    const [bg, color] = LEVEL_COLOR[l.level] || ['#f1f5f9', '#64748b'];
                    return (
                      <tr key={l.id} style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
                        <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: 12 }}>{fmtDate(l.created_at)}</td>
                        <td style={td}>
                          <span style={{ background: bg, color, padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
                            {LEVEL_LABEL[l.level] || l.level}
                          </span>
                        </td>
                        <td style={{ ...td, fontWeight: 600, color: 'var(--text-primary)' }}>{humanSource(l.source)}</td>
                        <td style={{ ...td, maxWidth: 320 }}>
                          <div style={{ fontWeight: 500 }}>{humanLabel(l.source, l.message)}</div>
                          {humanLabel(l.source, l.message) !== l.message && (
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: 2 }}>
                              {l.message}
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, maxWidth: 480 }}>
                          <ContextChips raw={l.context} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 6px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)' };
const td = { padding: '10px 6px', color: 'var(--text-primary)' };
const selectStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)' };
